'use client';

import { IVacancy } from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue, useAtom } from 'jotai';
import React from 'react';
import { MouseParallax } from 'react-just-parallax';
import Link from 'next/link';
import AnimatedTitle from '../../components/AnimatedTitle';
import Button, { ButtonVariants } from '../../components/Button';
import CareerTabs from '../../components/CareerTabs';
import Ellipse from '../../components/Ellipse';
import Img from '../../components/Img';
import { theme, Theme } from '../../store/theme';
import isVacanciesModalOpen from '../../store/vacanciesForm';
import styles from './Vacancies.module.scss';
import VacanciesFormModal from './VacanciesFormModal';
import { GAEmit, GAEvents } from '../../gtagHelper';
import { EllipseVariants } from '../../../constants';

type VacanciesProps = {
  vacancies: IVacancy[],
};
const Vacancies = ({ vacancies }: VacanciesProps) => {
  const isVacanciesEmpty = vacancies.length === 0;
  const [isModalOpen, setIsModalOpen] = useAtom(isVacanciesModalOpen);
  const appTheme = useAtomValue<Theme>(theme);

  const handleApplyFormClick = () => {
    setIsModalOpen(true);
    GAEmit(GAEvents.applyFormClick);
  };

  return (
    <main className={classnames(styles.vacanciesPage)}>
      <div className={classnames(styles.vacancies, 'container', {
        [styles.vacancies_empty]: isVacanciesEmpty,
      })}
      >

        <aside className={classnames(styles.sidebar, styles.sidebar_noVacancies)}>
          <div className={styles.ellipses}>
            <MouseParallax
              shouldPause={false}
              isAbsolutelyPositioned
              strength={0.04}
            >
              <Ellipse
                variant={EllipseVariants.DRAW}
                size={823}
                className={classnames(styles.ellipse, styles.ellipse_draw)}
              />
            </MouseParallax>
            <MouseParallax
              shouldPause={false}
              isAbsolutelyPositioned
              strength={0.2}
            >
              <Ellipse
                variant={EllipseVariants.NOISE}
                className={classnames(styles.ellipse, styles.ellipse_noise)}
                size={129}
              />
            </MouseParallax>
            <MouseParallax
              shouldPause={false}
              isAbsolutelyPositioned
              strength={0.08}
            >
              <Ellipse
                variant={EllipseVariants.GRADIENT}
                className={classnames(styles.ellipse, styles.ellipse_gradient)}
                size={51}
              />
            </MouseParallax>
          </div>
          <div className={styles.tabsWrap}>
            <CareerTabs className={styles.tabs} />
          </div>
          <div className={styles.titleBlock}>
            {!isVacanciesEmpty && <p className={styles.subtitle}>Become a part of SapientPro</p>}
            <h1 className={styles.title}>
              <AnimatedTitle
                title={isVacanciesEmpty ? 'Your Next Team Could Be Us' : 'Check Out Our Awesome Vacancies'}
              />
            </h1>
            {isVacanciesEmpty && (
              <>
                <p className={classnames(styles.subtitle, styles.noVacancies)}>
                  Intrigued by our culture and projects?
                  We keep in touch with pros across design, code, and strategy.
                  Send your CV – when a challenge that matches your skills arrives, you’ll be on our shortlist.
                </p>
                <Button
                  variant={ButtonVariants.OUTLINED}
                  className={styles.subscribeButton}
                  icon={(
                    <svg>
                      <use
                        xlinkHref='/media/icon-nest.svg#iconNest'
                        href='/media/icon-nest.svg#iconNest'
                      />
                    </svg>
                  )}
                  iconPosition='right'
                  iconSize={{ width: 24, height: 24 }}
                  onClick={handleApplyFormClick}
                >
                  Apply form
                </Button>
              </>
            )}
          </div>
          {isVacanciesEmpty && (
            <Img
              className={styles.noVacanciesImage}
              src={`/media/noVacancies_${appTheme}.svg`}
              alt='no vacancies'
              width={325}
              height={235}
            />
          )}
        </aside>
        <div className={styles.vacanciesList}>
          {vacancies.map(vacancy => (
            <Link
              href={`/career/vacancies/${vacancy.slug}`}
              key={vacancy.id}
              className={styles.vacancyCard}
            >
              <h2 className={styles.vacancyCard__title}>{vacancy.title}</h2>
              <p className={styles.vacancyCard__description}>{vacancy.description}</p>
              <footer className={styles.vacancyCard__footer}>
                <p className={styles.vacancyCard__info}>
                  <svg>
                    <use
                      xlinkHref='/media/map-pin.svg#mapPinSVG'
                      href='/media/map-pin.svg#mapPinSVG'
                    />
                  </svg>
                  {vacancy.location}
                </p>
                <p className={styles.vacancyCard__info}>
                  <svg>
                    <use
                      xlinkHref='/media/clock.svg#clockSVG'
                      href='/media/clock.svg#clockSVG'
                    />
                  </svg>
                  {vacancy.workSchedule}
                </p>
              </footer>
            </Link>
          ))}
        </div>
        {isModalOpen && <VacanciesFormModal />}
      </div>
    </main>
  );
};

export default Vacancies;
